import clsx from 'clsx'
import React, { ReactNode, useRef } from 'react'
import { CSSProperties } from 'react'

import styles from './styles.module.scss'

import { IconSvg } from '../../atoms'
import { breakpoints, useMediaQuery } from '../../../lib/hooks'

interface ITabsProps<T> {
  tabs: ITabProps<T>[]
  activeTab: T | null
  onChangeActiveTab?: (arg: T) => void
  style?: CSSProperties
  styleLabel?: CSSProperties
  disabled?: boolean
  forceWebStyle?: boolean
  className?: string
  tabClassName?: string
  labelTabClassName?: string
  dataGtm?: string
}

export interface ITabProps<T> {
  label: ReactNode
  icon?: any
  value: T
  isLocked?: boolean
  onClick?: () => void
}

function Tabs<T extends string>({
  tabs,
  activeTab,
  onChangeActiveTab,
  style = {},
  styleLabel = {},
  disabled = false,
  forceWebStyle = false,
  className,
  tabClassName,
  dataGtm,
  labelTabClassName,
}: ITabsProps<T>) {
  const isMobile = useMediaQuery(breakpoints.newMobile)
  const tabRefs = useRef<(HTMLDivElement | null)[]>([])

  const handleTabClick = (tabValue: T) => {
    onChangeActiveTab && onChangeActiveTab(tabValue)
    const targetSection = document.getElementById(tabValue)
    if (targetSection) {
      targetSection.scrollIntoView({ behavior: 'smooth' })
    }
  }

  return (
    <div
      className={clsx(
        !isMobile || forceWebStyle
          ? styles.containerTabs
          : styles.mobileContainerTabs,
        className,
      )}
      style={style}
      data-gtm={dataGtm}
    >
      {tabs.map((tab, index) => (
        <div
          ref={(el) => {
            tabRefs.current[index] = el
          }}
          className={clsx(styles.containerTab, tabClassName, {
            [styles.active]: activeTab === tab.value,
            [styles.locked]: tab.isLocked || disabled,
          })}
          onClick={() => {
            if (tab.isLocked || disabled) {
              return
            }
            if (tab.onClick) {
              tab.onClick()
            }
            handleTabClick(tab.value)
          }}
          key={tab.value}
        >
          {tab.icon && <div className={styles.iconTab}>{tab.icon}</div>}
          <span
            className={clsx(styles.labelTab, labelTabClassName)}
            style={styleLabel}
          >
            {tab.label}
          </span>
          {tab.isLocked && (
            <div className={styles.iconLocked}>
              <IconSvg.Lock height={10} />
            </div>
          )}
        </div>
      ))}
    </div>
  )
}

export default Tabs
